<script setup lang="ts">
/**
 * Final Dividends (B32112) — SG 일반 프리셋 Studio 패널.
 *   주주구성 자동판별(Multiple / Sole Individual / Sole Corporate) → 문서 세트 결정.
 *   Dividend Voucher = 주주 1인당 1부(수동 add/remove). DR = 이사 전원 서명([[Signers]]),
 *   Notice/Cover/Voucher/Notification = 이사 1인([[Signing_Director_Signature]]).
 *   자체완결 cdd(useAgmFill) + normalizeFinalDividendsForWord. 문서 생성은 prepare-documents.
 */
import { sgAppliedAddressLine } from '@/features/startTask/preset_task/sg/preset/utils/sgAppliedAddressLine';
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, bareKey, scopeOf, typeOf } from './final-dividends/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from './final-dividends/cdd/agmSignersContext';
import FinalDividendsPackageDoc from './final-dividends/FinalDividendsPackageDoc.vue';
import { normalizeFinalDividendsForWord } from './final-dividends/utils/normalizeFinalDividendsForWord';
import { MEMBER_OWNED } from './final-dividends/docs/members';
import type { DivScenario } from './final-dividends/docs/scenario';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  appliedAddress?: {
    key: string; line?: string; line1?: string; line2?: string; line3?: string;
    town?: string; state?: string; postcode?: string; country?: string; seq?: number;
  } | null;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const docRef = ref<InstanceType<typeof FinalDividendsPackageDoc> | null>(null);
const autofilledKeys = ref<Set<string>>(new Set());
const lastAutofillCd = ref('');
const autoLocal = ref<{ scenario: DivScenario; memberIds: number[]; memberSummary: string[] }>({ scenario: 'multiple', memberIds: [1], memberSummary: [] });
const taskConfigSteps = ref<string[]>([]);

const TASK_CODE = 'B32112';

function isoDate(v: unknown): string {
  const s = String(v ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  return m ? `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}` : s;
}
let signerSeq = 0;
function mkSigner(name: string, title: string, role = 'director'): SignerEntry {
  return { id: `fd-sg-${signerSeq++}`, name: String(name || '').trim(), title, role, isManual: true, showTitle: true, _signerLayout: 'box' } as SignerEntry;
}
function autoSigners(names: Array<{ name: unknown; title: string }>): any[] | undefined {
  const out: any[] = [];
  names.forEach((n) => { const nm = String(n.name ?? '').trim(); if (nm) out.push(mkSigner(nm, n.title)); });
  return out.length ? out : undefined;
}

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);
provide(AGM_ENTITY_CD, entityCd);

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  const pct = Math.min(100, Math.round((fill.filledCount.value / total) * 100));
  emit('progress-change', pct);
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

function lookupRoleFor(key: string): { role: string; scoped: boolean } {
  const b = bareKey(key);
  if (b === 'chairman_name' || b === 'corporate_representative_name') return { role: 'DIRECTOR', scoped: true };
  if (b === 'member_name') return { role: 'SHAREHOLDER', scoped: true };
  return { role: 'CONTACT', scoped: false };
}
fill.setActivateHandler((key: string) => {
  const t = typeOf(key);
  if (t === 'entity') { emit('change-entity'); return; }
  if (t === 'person') {
    const { role, scoped } = lookupRoleFor(key);
    emit('request-person-lookup', {
      fieldGroup: 'sg-final-dividends', key: bareKey(key), scopedKey: key,
      defaultRole: role, lookupType: 'officer', entityCd: scoped ? entityCd.value : '',
    });
  }
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });

function memberIsCorporate(m: any): boolean {
  if (!m) return false;
  if (m.isIndividual === true) return false;
  if (m.isIndividual === false || m.isCorporate === true) return true;
  return /\b(pte|ltd|limited|llp|inc|corp|holdings?|bhd|berhad)\b/i.test(String(m.name || ''));
}
function memberAddress(m: any): string {
  return String(m?.registeredOfficeAddress || m?.address || m?.residentialAddress || '').trim();
}
/** getContactDetail 응답에서 주소 한 줄 추출(개인=거주지 / 법인=등록사무소). */
function contactAddress(c: any): string {
  const direct = String(
    c?.residentialAddress || c?.primaryAddress || c?.registeredAddress
    || c?.registeredOfficeAddress || c?.contactAddress || c?.address || '',
  ).trim();
  if (direct) return direct;
  const arr = Array.isArray(c?.addresses) ? c.addresses : [];
  if (arr.length) {
    const a = arr[0];
    if (typeof a === 'string') return a.trim();
    return [
      a.line1 || a.addressLine1 || a.block, a.line2 || a.addressLine2 || a.street,
      [a.postalCode || a.postcode, a.city].filter(Boolean).join(' '), a.country,
    ].map((s: any) => String(s || '').trim()).filter(Boolean).join(', ');
  }
  return '';
}

// ── 자동채움: 회사 선택 → 회사·주소·FYE·주주(멤버)·이사 반영 + 시나리오 판별 ──
async function autofillFromEntity(entityCdRaw: string) {
  const cd = String(entityCdRaw || '').trim();
  if (!cd || cd === lastAutofillCd.value) return;
  const entityChanged = !!lastAutofillCd.value && lastAutofillCd.value !== cd;
  lastAutofillCd.value = cd;
  for (const k of autofilledKeys.value) fill.setValue(k, '');
  if (entityChanged) {
    signersCtx.clearAll();
    for (const k of Object.keys(fill.values)) { if (/^mem_\d+\//.test(k)) fill.setValue(k, ''); }
  }
  const written = new Set<string>();
  const set = (k: string, v: unknown) => { const s = String(v ?? '').trim(); if (s) { fill.setValue(k, s); written.add(k); } };

  let members: any[] = [];
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_registration_no', src.registrationNo);
    if (src.registeredOfficeAddress) set('registered_office_address', src.registeredOfficeAddress);
    set('financial_year_end', isoDate(src.financialYearEnd));
    members = Array.isArray(src.members) ? src.members.filter((m: any) => m?.name) : [];
  } catch (e) { console.warn('[FinalDividends] agm-source failed', e); }

  // Directors (SG) — officers-by-entity-all 중 이사(positionCd B30027 / positionName 'director').
  //   entityMyApi.getEntityOfficer 는 MY 전용이라 SG 엔티티에선 빈 결과 → 자동 서명자가 안 들어감.
  const directorNames: string[] = [];
  try {
    const res: any = await docGenApi.getEntityOfficersAll(cd);
    const officers: any[] = res?.data?.officers ?? res?.data?.data?.officers ?? [];
    for (const o of officers) {
      const positions: any[] = Array.isArray(o?.positions) ? o.positions : [];
      const isDir = positions.some((p) => String(p?.positionCd || '') === 'B30027' || /director/i.test(String(p?.positionName || '')));
      if (!isDir) continue;
      const nm = String(o?.contactName || o?.name || o?.fullName || '').trim();
      if (nm && !directorNames.includes(nm)) directorNames.push(nm);
    }
  } catch (e) { console.warn('[FinalDividends] officers-by-entity-all failed', e); }
  if (directorNames.length) {
    const board = directorNames.map((nm) => mkSigner(nm, 'Director'));
    if (!signersCtx.signersOf('dr_Signers').length) signersCtx.setSigners('dr_Signers', [...board]);
    if (!signersCtx.signersOf('signing_director_Signers').length) signersCtx.setSigners('signing_director_Signers', [mkSigner(directorNames[0], 'Director')]);
    set('chairman_name', directorNames[0]);
  }

  // 시나리오 판별 + 멤버 채움
  let scenario: DivScenario = 'multiple';
  const memberIds: number[] = [];
  const memberEntries: Array<{ id: number; m: any }> = [];
  const firstDirector = directorNames[0] || '';
  if (members.length <= 1) {
    const m = members[0];
    scenario = m && memberIsCorporate(m) ? 'sole_corporate' : 'sole_individual';
    if (m) {
      set('member_name', m.name);
      if (scenario === 'sole_corporate') set('holding_company_name', m.name);
      set('mem_1/member_name', m.name);
      set('mem_1/dividend_number', '1');
      const sShares = Number(m.sharesTotal || m.sharesOrdinary || 0);
      if (sShares > 0) set('mem_1/number_of_shares', sShares.toLocaleString());
      memberIds.push(1); memberEntries.push({ id: 1, m });
    } else {
      memberIds.push(1);
    }
  } else {
    scenario = 'multiple';
    members.forEach((m, i) => {
      const id = i + 1;
      set(`mem_${id}/member_name`, m.name);
      set(`mem_${id}/dividend_number`, String(id));
      const shares = Number(m.sharesTotal || m.sharesOrdinary || 0);
      if (shares > 0) set(`mem_${id}/number_of_shares`, shares.toLocaleString());
      memberIds.push(id); memberEntries.push({ id, m });
    });
  }
  if (!memberIds.length) memberIds.push(1);

  // 각 Voucher(멤버)의 자동 서명자 = 첫 이사 (스코프 키라 전역 seed 로는 안 들어감)
  if (firstDirector) {
    for (const { id } of memberEntries) {
      const sk = `mem_${id}/signing_director_Signers`;
      if (!signersCtx.signersOf(sk).length) signersCtx.setSigners(sk, [mkSigner(firstDirector, 'Director')]);
    }
  }

  // 멤버 주소 보강 — agm-source 멤버엔 주소가 없어 contactCd 로 상세 조회(개인=거주지/법인=등록사무소).
  await Promise.all(memberEntries.map(async ({ id, m }) => {
    let addr = memberAddress(m);
    const contactCd = String(m?.contactCd || '').trim();
    if (!addr && contactCd) {
      try {
        const cRes: any = await docGenApi.getContactDetail(cd, contactCd, { addressScope: 'person' });
        addr = contactAddress(cRes?.data?.data || cRes?.data || cRes || {});
      } catch (e) { console.warn('[FinalDividends] member address enrich failed', e); }
    }
    if (addr) {
      set(`mem_${id}/member_address`, addr);
      if (memberIds.length <= 1) set('member_address', addr);
    }
  }));

  // 게이트에 보여줄 식별된 주주 명단(저장된 정보 기준).
  const memberSummary = members.map((m: any) => {
    const nm = String(m?.name || '').trim();
    if (!nm) return '';
    const kind = memberIsCorporate(m) ? 'Corporate' : 'Individual';
    const shares = Number(m?.sharesTotal || m?.sharesOrdinary || 0);
    return shares > 0 ? `${nm} · ${kind} · ${shares.toLocaleString()} shares` : `${nm} · ${kind}`;
  }).filter(Boolean);

  autoLocal.value = { scenario, memberIds, memberSummary };
  autofilledKeys.value = written;
  emitProgress();
}

watch(() => props.chatFormData, (fd) => {
  if (!fd) return;
  const nm = String(fd.company_name || fd.entityName || '').trim();
  if (nm && fill.values.company_name !== nm) fill.setValue('company_name', nm);
  const cd = String(fd.entityCd || fd.icpCd || '').trim();
  if (cd) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd) void autofillFromEntity(cd); }, { immediate: true });

watch(() => props.selectedPerson, (p) => {
  if (!p || !fill.activeKey.value) return;
  fill.applyPersonToActive(p as Record<string, any>);
  emitProgress();
});
watch(() => props.selectedAddress, (a) => {
  if (!a?.value || !fill.activeKey.value) return;
  fill.setValue(fill.activeKey.value, a.value);
  emitProgress();
});
watch(() => props.appliedAddress, (a) => {
  if (!a?.key) return;
  const line = sgAppliedAddressLine(a);
  if (line) { fill.setValue(a.key, line); emitProgress(); }
}, { deep: true });

// ── 배당액 자동 계산 ─────────────────────────────────────────────
//   Multiple: 각 멤버 Amount 를 운영자가 입력 → TOTAL(total_dividend_amount) + dividend_amount = **합계** 자동.
//   Sole    : 총 배당액(dividend_amount) 입력 → 그 1인 amount + total 미러(전액).
//   총 배당액 자체는 업무 결정값이라 엔티티에서 못 가져온다(운영자 입력).
const autoAmountKeys = ref<Set<string>>(new Set());
let recomputing = false;
function parseAmt(s: unknown): number {
  const n = parseFloat(String(s ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}
function amtPrefix(s: unknown): string {
  const m = String(s ?? '').match(/^[^0-9.]+/);
  return m ? m[0] : '';
}
function fmtAmt(n: number, prefix: string): string {
  const body = n.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return prefix ? `${prefix}${body}` : body;
}
function currentMemberIds(): number[] {
  const l: any = docRef.value?.gatherLocalState?.();
  const ids = Array.isArray(l?.memberIds) && l.memberIds.length ? l.memberIds : autoLocal.value.memberIds;
  return (ids && ids.length) ? ids : [1];
}
function currentScenario(): DivScenario {
  const l: any = docRef.value?.gatherLocalState?.();
  return (l?.scenario || autoLocal.value.scenario || 'multiple') as DivScenario;
}
function recomputeDividendAmounts() {
  if (recomputing) return;
  const ids = currentMemberIds();
  recomputing = true;
  try {
    if (currentScenario() === 'multiple') {
      // TOTAL = 각 멤버 Amount 합계 → total_dividend_amount + dividend_amount
      let prefix = ''; let sum = 0; let any = false;
      for (const id of ids) {
        const v = String(fill.values[`mem_${id}/amount_to_receive`] ?? '').trim();
        if (v) { any = true; if (!prefix) prefix = amtPrefix(v); sum += parseAmt(v); }
      }
      if (any) {
        const t = fmtAmt(sum, prefix);
        fill.setValue('total_dividend_amount', t);
        fill.setValue('dividend_amount', t);
      }
    } else {
      // Sole: 총 배당액(dividend_amount) → 그 1인 amount + total 미러(전액)
      const totalStr = String(fill.values.dividend_amount ?? '').trim();
      if (parseAmt(totalStr)) {
        fill.setValue('total_dividend_amount', totalStr);
        const id = ids[0] ?? 1;
        const key = `mem_${id}/amount_to_receive`;
        if (!String(fill.values[key] ?? '').trim() || autoAmountKeys.value.has(key)) {
          fill.setValue(key, totalStr); autoAmountKeys.value.add(key);
        }
      }
    }
  } finally { recomputing = false; }
  emitProgress();
}
// Multiple: 멤버 Amount 변경 → 합계 재계산. Sole: dividend_amount 변경 → 미러.
watch(() => currentMemberIds().map((id) => fill.values[`mem_${id}/amount_to_receive`] ?? '').join('|'), () => recomputeDividendAmounts());
watch(() => fill.values.dividend_amount, () => recomputeDividendAmounts());
watch(() => autoLocal.value.memberIds, () => recomputeDividendAmounts(), { deep: true });

onMounted(async () => {
  emitProgress();
  try {
    const cfg: any = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : [];
  } catch { /* ignore */ }
});

function gatherState() {
  return {
    values: { ...fill.exportPayload() },
    local: docRef.value?.gatherLocalState?.() || null,
    signers: signersCtx.exportAll(),
    entityCd: entityCd.value,
    taskTitle: effectiveTitle.value,
  };
}
function restoreState(s: any) {
  if (!s) return;
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v));
  }
  if (s.signers) signersCtx.importAll(s.signers);
  if (s.local) docRef.value?.restoreLocalState?.(s.local);
}

// ── 문서 분류(시나리오별) ──
type FdKind = 'dr' | 'notice' | 'minutes' | 'rep_minutes' | 'notification' | 'cover_letter' | 'voucher' | 'other';
interface FdClass { kind: FdKind; forScenario: DivScenario | 'any'; }
function classifyFdDoc(name: string): FdClass {
  const n = String(name || '').toLowerCase().replace(/[_\-.]+/g, ' ');
  const sc: DivScenario | 'any' =
    /multiple/.test(n) ? 'multiple'
      : /sole\s*individual|individual\s*member/.test(n) ? 'sole_individual'
        : /sole\s*corporate|corporate\s*member/.test(n) ? 'sole_corporate'
          : 'any';
  if (/dividend voucher/.test(n)) return { kind: 'voucher', forScenario: 'any' };
  if (/cover letter/.test(n)) return { kind: 'cover_letter', forScenario: 'sole_corporate' };
  if (/notification of resolution/.test(n)) return { kind: 'notification', forScenario: 'sole_corporate' };
  if (/representative minutes|179/.test(n)) return { kind: 'rep_minutes', forScenario: 'sole_corporate' };
  if (/directors.*resolution|resolution.*final dividend/.test(n)) return { kind: 'dr', forScenario: sc };
  if (/minutes/.test(n)) return { kind: 'minutes', forScenario: sc };
  if (/notice/.test(n)) return { kind: 'notice', forScenario: sc };
  return { kind: 'other', forScenario: 'any' };
}

function companyFormData(): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) { if (!k.includes('/')) out[k] = v; }
  return out; // normalize 는 호출부에서 (shareholders 등 주입 후)
}
function scopedFormData(scopePrefix: string, owned: string[]): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) {
    if (k.includes('/')) continue;
    if (owned.includes(bareKey(k))) continue;
    out[k] = v;
  }
  for (const [k, v] of Object.entries(base)) {
    if (!k.startsWith(scopePrefix)) continue;
    const bk = bareKey(k);
    const sv = String(v ?? '').trim();
    if (sv === '' && bk in out && String(out[bk] ?? '').trim() !== '') continue;
    out[bk] = v;
  }
  return out;
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = String(props.selection.task.TASK_CODE || TASK_CODE).trim() || TASK_CODE;
    const local: any = docRef.value?.gatherLocalState?.() || { scenario: 'multiple', memberIds: [1] };
    const scenario: DivScenario = local.scenario || 'multiple';
    const memberIds: number[] = Array.isArray(local.memberIds) && local.memberIds.length ? local.memberIds : [1];

    // 이사 서명(전원 = [[Signers]] / 단일 = [[Signing_Director_Signature]])
    const boardSigners = signersCtx.signersOf('dr_Signers').length
      ? signersCtx.signersOf('dr_Signers')
      : (autoSigners([{ name: fill.values.chairman_name, title: 'Director' }]) || []);
    const singleSigGlobal = signersCtx.signersOf('signing_director_Signers').length
      ? signersCtx.signersOf('signing_director_Signers')
      : (boardSigners.length ? [boardSigners[0]] : autoSigners([{ name: fill.values.chairman_name, title: 'Director' }]) || []);

    // 회사 단위 formData + Minutes(Multiple) 주주 배열
    const companyBaseRaw = companyFormData();
    if (scenario === 'multiple') {
      companyBaseRaw.shareholders = memberIds.map((id) => ({
        Member_Name: String(fill.values[`mem_${id}/member_name`] || '').trim(),
        Amount_To_Receive: String(fill.values[`mem_${id}/amount_to_receive`] || '').trim(),
      })).filter((r) => r.Member_Name);
      // multiple 은 top-level 단일 member_name 없음(루프 충돌 방지)
      delete companyBaseRaw.member_name;
    }
    const companyBase = normalizeFinalDividendsForWord(companyBaseRaw);
    const companyLabel = String(companyBase.Company_Name || companyBase.company_name || 'Company');

    const officerData = {
      entityCd: entityCd.value || null,
      icpCd: entityCd.value || null,
      presetForm: gatherState(),
      docType: 'SG_FINAL_DIVIDENDS',
      formData: companyBase,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'Final Dividends',
          taskTitle: effectiveTitle.value,
          taskFlow: (() => {
            const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
            return { createDocument: steps.length === 0 ? true : steps.includes('doc'), requestTSign: steps.includes('t-sign'), proceedWithEFiling: steps.includes('e-filing') };
          })(),
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) { progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`; creating.value = false; return; }
    }

    progress.value = 'Preparing documents…';
    const rawDocs = props.selection.documents || [];
    const seenTmpl = new Set<number>();
    const docs = rawDocs.filter((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return true;
      if (seenTmpl.has(tid)) return false;
      seenTmpl.add(tid); return true;
    });
    const stripSuffix = (s: string) => s.split(' — ')[0].trim();
    type DocSave = { templateId: number; label: string; personName: string; formData: Record<string, any>; signers?: any[] };
    const docsToSave: DocSave[] = [];
    function pushOnce(d: any, formData: Record<string, any>, person: string, suffix = '', signers?: any[]) {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return;
      const baseLabel = stripSuffix(String(d?.templateName || d?.docCode || 'Document').replace(/\.docx?$/i, ''));
      docsToSave.push({
        templateId: tid,
        label: suffix ? `${baseLabel} — ${suffix}` : baseLabel,
        personName: person,
        formData: normalizeFinalDividendsForWord(formData),
        signers: signers?.length ? signers : undefined,
      });
    }

    for (const d of docs) {
      const { kind, forScenario } = classifyFdDoc((d as any).templateName || (d as any).docCode || '');
      if (kind === 'other') continue;
      if (forScenario !== 'any' && forScenario !== scenario) continue;

      if (kind === 'voucher') {
        for (const id of memberIds) {
          const fd = scopedFormData(`mem_${id}/`, MEMBER_OWNED);
          const vSig = signersCtx.signersOf(`mem_${id}/signing_director_Signers`).length
            ? signersCtx.signersOf(`mem_${id}/signing_director_Signers`) : singleSigGlobal;
          fd.Signing_Director_Signature = vSig; // 렌더용([[Signing_Director_Signature]])
          const person = String(fd.member_name || `Member ${id}`);
          // 다건일 때 문서명에 멤버 이름을 붙인다(예: "Dividend Voucher — ALICE TAN"). 이름 없으면 Member N.
          //   서명자는 DocSave.signers 로도 넘겨 SIGNER_JSON 저장 → edit 화면·카운트·T-Sign 에 반영.
          pushOnce(d, fd, person, memberIds.length > 1 ? person : '', vSig);
        }
        continue;
      }
      if (kind === 'dr') {
        pushOnce(d, { ...companyBaseRaw }, companyLabel, '', boardSigners);
        continue;
      }
      // notice / cover_letter / notification — 단일 이사 서명 블록
      if (kind === 'notice' || kind === 'cover_letter' || kind === 'notification') {
        const fd = { ...companyBaseRaw };
        fd.Signing_Director_Signature = singleSigGlobal; // 렌더용
        const person = (kind === 'cover_letter' || kind === 'notification')
          ? String(companyBaseRaw.member_name || companyLabel) : companyLabel;
        // 서명자를 DocSave.signers 로도 전달 → SIGNER_JSON 저장(edit 화면·카운트 반영).
        pushOnce(d, fd, person, '', singleSigGlobal);
        continue;
      }
      // minutes / rep_minutes — 서명 블록 없음(밑줄 + 이름)
      pushOnce(d, { ...companyBaseRaw }, companyLabel);
    }

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((r) => r.json());
        if (!saveRes?.success) { progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`; }
      } catch (err: any) { progress.value = `⚠ Document save failed: ${err?.message || err}`; }
    }
    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
void scopeOf;
</script>

<template>
  <div class="fd-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Final Dividends' }}</div>
        <div class="co-subtitle">Final Dividends — live document package</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>

    <div v-if="!studio" class="co-title-row">
      <v-text-field v-model="titleModel" density="compact" variant="outlined" hide-details label="Task Title" />
    </div>

    <div class="co-doc">
      <FinalDividendsPackageDoc ref="docRef" :auto-local="autoLocal" />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || creating" @click="createTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.fd-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.fd-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
